import type { Language } from "../contexts/types";

export type SubscriptionPlan = "pro" | "premium";
export type SubscriptionBilling = "monthly" | "annual";
export type SubscriptionCurrency = "BRL" | "USD";

type LocalizedCopy = {
  activateLicense: string;
  choosePlan: string;
  monthly: string;
  annual: string;
  twoMonthsFree: string;
  comingSoon: string;
  paymentMethod: string;
  creditCard: string;
  pix: string;
  back: string;
  close: string;
  openingCheckout: string;
  pixComingSoon: string;
  subscribePlan: (planLabel: string) => string;
  annualNote: string;
  authStepTitle: string;
  confirmStepTitle: string;
  googleSignIn: string;
  emailPlaceholder: string;
  passwordPlaceholder: string;
  createAccountAndContinue: string;
  signInAndContinue: string;
  alreadyHaveAccount: string;
  noAccount: string;
  createAccount: string;
  signIn: string;
  accountLabel: string;
  continueCheckout: string;
  useCardOption: string;
  cardNotConfigured: string;
  pix30Days: string;
  cardRecurring: string;
};

const PRICE_TABLE: Record<SubscriptionCurrency, Record<SubscriptionPlan, Record<SubscriptionBilling, string>>> = {
  BRL: {
    pro: {
      monthly: "R$99",
      annual: "R$990",
    },
    premium: {
      monthly: "R$199",
      annual: "R$1.990",
    },
  },
  USD: {
    pro: {
      monthly: "US$19",
      annual: "US$190",
    },
    premium: {
      monthly: "US$39",
      annual: "US$390",
    },
  },
};

const PERIOD_LABELS: Record<Language, Record<SubscriptionBilling, string>> = {
  pt: {
    monthly: "/mês",
    annual: "/ano",
  },
  en: {
    monthly: "/mo",
    annual: "/yr",
  },
  es: {
    monthly: "/mes",
    annual: "/año",
  },
};

const MONTHLY_EQUIV: Record<Language, Record<SubscriptionPlan, string>> = {
  pt: {
    pro: "equivale a R$82,50/mês",
    premium: "equivale a R$165,83/mês",
  },
  en: {
    pro: "equiv. US$16.50/mo",
    premium: "equiv. US$33.00/mo",
  },
  es: {
    pro: "equiv. US$16,50/mes",
    premium: "equiv. US$33,00/mes",
  },
};

const COPY: Record<Language, LocalizedCopy> = {
  pt: {
    activateLicense: "Ativar licença",
    choosePlan: "Escolha seu plano",
    monthly: "Mensal",
    annual: "Anual",
    twoMonthsFree: "2 meses grátis",
    comingSoon: "Em breve",
    paymentMethod: "Método de pagamento",
    creditCard: "Cartão",
    pix: "Pix",
    back: "Voltar",
    close: "Fechar",
    openingCheckout: "Abrindo checkout...",
    pixComingSoon: "Pix disponível em breve",
    subscribePlan: (planLabel) => `Assinar ${planLabel}`,
    annualNote: "Planos anuais já configurados.",
    authStepTitle: "Criar conta",
    confirmStepTitle: "Confirmar assinatura",
    googleSignIn: "Continuar com Google",
    emailPlaceholder: "Seu e-mail",
    passwordPlaceholder: "Senha (mín. 6 caracteres)",
    createAccountAndContinue: "Criar conta e continuar",
    signInAndContinue: "Entrar e continuar",
    alreadyHaveAccount: "Já tem conta?",
    noAccount: "Não tem conta?",
    createAccount: "Criar conta",
    signIn: "Entrar",
    accountLabel: "Conta",
    continueCheckout: "Continuar checkout",
    useCardOption: "Use a opção Cartão",
    cardNotConfigured: "Chave Pix não configurada pelo administrador.",
    pix30Days: "Pix ativa por 30 dias · renovação manual.",
    cardRecurring: "Cartão ativa assinatura recorrente · cancele quando quiser.",
  },
  en: {
    activateLicense: "Activate license",
    choosePlan: "Choose your plan",
    monthly: "Monthly",
    annual: "Annual",
    twoMonthsFree: "2 months free",
    comingSoon: "Coming soon",
    paymentMethod: "Payment method",
    creditCard: "Card",
    pix: "Pix",
    back: "Back",
    close: "Close",
    openingCheckout: "Opening checkout...",
    pixComingSoon: "Pix coming soon",
    subscribePlan: (planLabel) => `Subscribe ${planLabel}`,
    annualNote: "Annual plans are already configured.",
    authStepTitle: "Create account",
    confirmStepTitle: "Confirm subscription",
    googleSignIn: "Continue with Google",
    emailPlaceholder: "Your email",
    passwordPlaceholder: "Password (min. 6 characters)",
    createAccountAndContinue: "Create account and continue",
    signInAndContinue: "Sign in and continue",
    alreadyHaveAccount: "Already have an account?",
    noAccount: "Don't have an account?",
    createAccount: "Create account",
    signIn: "Sign in",
    accountLabel: "Account",
    continueCheckout: "Continue checkout",
    useCardOption: "Use the Card option",
    cardNotConfigured: "Pix key not configured by the administrator.",
    pix30Days: "Pix active for 30 days · manual renewal.",
    cardRecurring: "Card activates recurring subscription · cancel anytime.",
  },
  es: {
    activateLicense: "Activar licencia",
    choosePlan: "Elige tu plan",
    monthly: "Mensual",
    annual: "Anual",
    twoMonthsFree: "2 meses gratis",
    comingSoon: "Próximamente",
    paymentMethod: "Método de pago",
    creditCard: "Tarjeta",
    pix: "Pix",
    back: "Volver",
    close: "Cerrar",
    openingCheckout: "Abriendo checkout...",
    pixComingSoon: "Pix disponible pronto",
    subscribePlan: (planLabel) => `Suscribirse a ${planLabel}`,
    annualNote: "Los planes anuales ya están configurados.",
    authStepTitle: "Crear cuenta",
    confirmStepTitle: "Confirmar suscripción",
    googleSignIn: "Continuar con Google",
    emailPlaceholder: "Tu correo",
    passwordPlaceholder: "Contraseña (mín. 6 caracteres)",
    createAccountAndContinue: "Crear cuenta y continuar",
    signInAndContinue: "Entrar y continuar",
    alreadyHaveAccount: "¿Ya tienes cuenta?",
    noAccount: "¿No tienes cuenta?",
    createAccount: "Crear cuenta",
    signIn: "Entrar",
    accountLabel: "Cuenta",
    continueCheckout: "Continuar checkout",
    useCardOption: "Usa la opción Tarjeta",
    cardNotConfigured: "La clave Pix no fue configurada por el administrador.",
    pix30Days: "Pix activa por 30 días · renovación manual.",
    cardRecurring: "La tarjeta activa una suscripción recurrente · cancela cuando quieras.",
  },
};

export function getSubscriptionCurrency(language: Language): SubscriptionCurrency {
  return language === "pt" ? "BRL" : "USD";
}

export function getSubscriptionPrice(
  language: Language,
  plan: SubscriptionPlan,
  billing: SubscriptionBilling,
): string {
  return PRICE_TABLE[getSubscriptionCurrency(language)][plan][billing];
}

export function getSubscriptionPeriodLabel(language: Language, billing: SubscriptionBilling): string {
  return PERIOD_LABELS[language][billing];
}

export function getSubscriptionMonthlyEquivalent(language: Language, plan: SubscriptionPlan): string {
  return MONTHLY_EQUIV[language][plan];
}

export function getSubscriptionCopy(language: Language): LocalizedCopy {
  return COPY[language] ?? COPY.pt;
}
